/**
 * Dérivation du libellé format (FR cm + EN inches) — réplique TS du helper
 * BE `catalog_service.derive_format_labels`.
 *
 * Pourquoi répliquer côté FE plutôt qu'appeler le BE en live ?
 *   - Preview synchrone à la saisie (chaque tap dans <input type=number>)
 *     sans débouncing serveur.
 *   - Algorithme tiny (snap noble + fallback décimal) — couvert par tests
 *     unitaires côté BE (`backend/tests/test_catalog_service.py`) ; cette
 *     implémentation suit la même logique exacte. Le BE reste source de
 *     vérité au save : la modal envoie `width_cm`/`height_cm` et le serveur
 *     calcule le label final.
 *
 * Si un drift apparaît (ex: ajout d'un noble ratio côté BE), update les
 * deux endroits dans la même PR.
 */

const _NOBLE_RATIOS: ReadonlyArray<readonly [string, number]> = [
  ['1:1', 1],
  ['3:2', 3 / 2],
  ['4:3', 4 / 3],
  ['5:4', 5 / 4],
  ['7:5', 7 / 5],
  ['√2:1', Math.SQRT2],
  ['16:9', 16 / 9],
  ['16:10', 16 / 10],
  ['21:9', 21 / 9],
]

const _RATIO_SNAP_TOLERANCE = 0.02
const _INCH_PER_CM = 1 / 2.54

function fmtCm(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '?'
  if (value === Math.trunc(value)) return String(Math.trunc(value))
  // 1 décimale max sans zéros traînants : 29.7 → "29.7", 30.0 → "30"
  return value.toFixed(1).replace(/\.?0+$/, '')
}

function fmtIn(valueCm: number): string {
  if (!Number.isFinite(valueCm) || valueCm <= 0) return '?'
  return (valueCm * _INCH_PER_CM).toFixed(1)
}

export function ratioString(widthCm: number, heightCm: number): string {
  if (!Number.isFinite(widthCm) || !Number.isFinite(heightCm)) return '?'
  if (widthCm <= 0 || heightCm <= 0) return '?'
  const larger = Math.max(widthCm, heightCm)
  const smaller = Math.min(widthCm, heightCm)
  const raw = larger / smaller
  const isLandscape = widthCm >= heightCm

  // Closest match within tolerance — A4 (1.4143) doit snap √2:1 plutôt que
  // 7:5 (1.4) bien que les deux soient dans la fenêtre 2 %.
  let bestLabel: string | null = null
  let bestErr = _RATIO_SNAP_TOLERANCE
  for (const [label, value] of _NOBLE_RATIOS) {
    const err = Math.abs(raw - value) / value
    if (err <= bestErr) {
      bestLabel = label
      bestErr = err
    }
  }
  if (bestLabel !== null) {
    if (isLandscape) return bestLabel
    const [left, right] = bestLabel.split(':')
    return `${right}:${left}`
  }
  return isLandscape ? `~${raw.toFixed(2)}:1` : `~1:${raw.toFixed(2)}`
}

export function deriveFormatLabels(
  widthCm: number,
  heightCm: number,
): { labelFr: string; labelEn: string } {
  const ratio = ratioString(widthCm, heightCm)
  return {
    labelFr: `${fmtCm(widthCm)}×${fmtCm(heightCm)} cm (${ratio})`,
    labelEn: `${fmtIn(widthCm)}×${fmtIn(heightCm)} in (${ratio})`,
  }
}
